// Preview mode utilities for CRA frontend
// - Stores a flag in sessionStorage
// - Installs a fetch patch that, when preview is enabled, appends status=draft to
//   specific Strapi endpoints relevant to the page being previewed

const PREVIEW_FLAG_KEY = 'p4a_preview_enabled';

export function isPreviewEnabled(): boolean {
  try {
    return sessionStorage.getItem(PREVIEW_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

export function enablePreview(): void {
  try {
    sessionStorage.setItem(PREVIEW_FLAG_KEY, '1');
  } catch {
    // ignore
  }
  patchFetchForPreview();
}

let patched = false;

export function patchFetchForPreview(): void {
  if (patched) return;
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;

  const base = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337/api';
  const baseNoApi = base.replace(/\/api$/, '');

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    // Determine if preview is active (session flag or ?preview=1)
    let previewEnabled = false;
    try {
      const inSession = sessionStorage.getItem(PREVIEW_FLAG_KEY) === '1';
      const hasQuery = new URLSearchParams(window.location.search).get('preview') === '1';
      previewEnabled = inSession || hasQuery;
    } catch {}
    if (!previewEnabled) {
      return originalFetch(input as any, init);
    }

    // Normalize to a string URL
    let urlStr = '';
    try {
      urlStr = typeof input === 'string' ? input : (input as URL).toString();
    } catch {
      return originalFetch(input as any, init);
    }

    // Only modify Strapi API calls
    if (!(urlStr.startsWith(base) || urlStr.startsWith(baseNoApi + '/api'))) {
      return originalFetch(input as any, init);
    }

    // Parse URL
    let parsed: URL;
    try {
      parsed = new URL(urlStr);
    } catch {
      return originalFetch(input as any, init);
    }

    // Determine preview type
    let t = '';
    try {
      const raw = sessionStorage.getItem('p4a_preview_target');
      if (raw) {
        const tgt = JSON.parse(raw || '{}') as { type?: string; slug?: string | null };
        t = String(tgt?.type || '').toLowerCase();
      }
    } catch {}
    if (!t) {
      const locPath = window.location.pathname;
      if (
        locPath.startsWith('/schedule') ||
        locPath.startsWith('/matching') ||
        locPath.startsWith('/perks') ||
        locPath.startsWith('/nominate-a-charity') ||
        locPath.startsWith('/submit')
      ) t = 'current-year-page';
      else if (locPath === '/' || locPath.startsWith('/home')) t = 'homepage';
      else if (locPath.startsWith('/who-we-are')) t = 'who-we-are';
      else if (locPath.startsWith('/how-it-works')) t = 'how-it-works-page';
      else if (locPath.startsWith('/videos')) t = 'videos-takeover-page';
    }

    // Decide if this endpoint should fetch drafts
    const p = parsed.pathname; // e.g., /api/homepage
    let shouldDraft = false;
    if (t === 'homepage' && p.startsWith('/api/homepage')) shouldDraft = true;
    else if (t === 'faq-page' && p.startsWith('/api/faq-page')) shouldDraft = true;
    else if (t === 'videos-takeover-page' && p.startsWith('/api/videos-takeover-page')) shouldDraft = true;
    else if (t === 'current-year-page' && p.startsWith('/api/active-campaign')) shouldDraft = true;
    else if (t === 'previous-year' && p.startsWith('/api/previous-year')) shouldDraft = true;
    else if (t === 'who-we-are' && p.startsWith('/api/who-we-are')) shouldDraft = true;
    else if (t === 'submission-confirmation-page' && p.startsWith('/api/submission-confirmation-page')) shouldDraft = true;
    else if (t === 'submission' && p.startsWith('/api/submissions')) shouldDraft = true;
    else if (t === 'page' && (p.startsWith('/api/pages') || p.startsWith('/api/page'))) shouldDraft = true;

    // Further scope: only draft on the page being previewed
    let onPreviewPage = true;
    try {
      const pth = sessionStorage.getItem('p4a_preview_path');
      if (pth) {
        const loc = window.location.pathname + window.location.search;
        onPreviewPage = loc.startsWith(pth);
      }
    } catch {}

    if (shouldDraft && onPreviewPage) {
      if (!parsed.searchParams.has('status')) parsed.searchParams.set('status', 'draft');
      const newInit: RequestInit = { ...(init || {}), cache: 'no-store' };
      return originalFetch(parsed.toString(), newInit);
    }

    return originalFetch(input as any, init);
  };
  patched = true;
}
